import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const FuelModal = ({ apiCall, vehicles, onClose, onSuccess, showError, showSuccess, editLog = null }) => {
  const [formData, setFormData] = useState({
    vehicleId: '',
    driverName: '',
    odometerReading: '',
    fuelType: '',
    volume: '',
    fuelDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [availableFuelTypes, setAvailableFuelTypes] = useState([]);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  useEffect(() => {
    if (editLog) {
      setFormData({
        vehicleId: editLog.VehicleID,
        driverName: editLog.DriverName,
        odometerReading: editLog.OdometerReading,
        fuelType: editLog.FuelType,
        volume: editLog.Volume,
        fuelDate: new Date(editLog.FuelDate).toISOString().split('T')[0],
        notes: editLog.Notes || ''
      });
      
      const vehicle = vehicles.find(v => v.VehicleID === editLog.VehicleID);
      if (vehicle) {
        updateAvailableFuelTypes(vehicle.FuelType);
      }
      
      if (editLog.FuelType && editLog.FuelDate) {
        fetchPriceForDate(editLog.FuelType, new Date(editLog.FuelDate).toISOString().split('T')[0]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLog, vehicles]);

  const fetchPriceForDate = async (fuelType, date) => {
    if (!fuelType || !date) {
      setPriceInfo(null);
      return;
    }

    try {
      setLoadingPrice(true);
      const data = await apiCall(`/gas/pricing/date?fuelType=${encodeURIComponent(fuelType)}&date=${date}`);
      if (data && data.PricePerLiter) {
        setPriceInfo({
          pricePerLiter: data.PricePerLiter,
          effectiveDate: data.EffectiveDate
        });
      } else {
        setPriceInfo(null);
      }
    } catch (error) {
      console.error('Error fetching price:', error);
      setPriceInfo(null);
    } finally {
      setLoadingPrice(false);
    }
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.VehicleID === parseInt(vehicleId));
    
    if (vehicle) {
      updateAvailableFuelTypes(vehicle.FuelType);
      const fuelTypes = getFuelTypesForVehicle(vehicle.FuelType);
      const selectedFuelType = fuelTypes.length > 0 ? fuelTypes[0] : '';
      
      setFormData({
        ...formData,
        vehicleId,
        fuelType: selectedFuelType,
        driverName: vehicle.PrimaryDriverName || ''
      });
      
      if (selectedFuelType && formData.fuelDate) {
        fetchPriceForDate(selectedFuelType, formData.fuelDate);
      }
    } else {
      setFormData({
        ...formData,
        vehicleId,
        fuelType: '',
        driverName: ''
      });
      setAvailableFuelTypes([]);
      setPriceInfo(null);
    }
  };

  const handleFuelTypeChange = (fuelType) => {
    setFormData({...formData, fuelType});
    if (fuelType && formData.fuelDate) {
      fetchPriceForDate(fuelType, formData.fuelDate);
    }
  };

  const handleDateChange = (date) => {
    setFormData({...formData, fuelDate: date});
    if (formData.fuelType && date) {
      fetchPriceForDate(formData.fuelType, date);
    }
  };

  const updateAvailableFuelTypes = (vehicleFuelType) => {
    const fuelTypes = getFuelTypesForVehicle(vehicleFuelType);
    setAvailableFuelTypes(fuelTypes);
  };

  const getFuelTypesForVehicle = (vehicleFuelType) => {
    if (!vehicleFuelType) return [];
    const fuelType = vehicleFuelType.toLowerCase();

    if (fuelType === 'gasoline') {
      return ['Gasoline 95', 'Gasoline 98'];
    }
    if (fuelType === 'diesel') {
      return ['Diesel'];
    }
    if (fuelType === 'electric') {
      return ['Electric'];
    }
    if (fuelType === 'hybrid') {
      return ['Gasoline 95', 'Gasoline 98', 'Electric'];
    }
    return [vehicleFuelType];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicleId || !formData.fuelType) {
      showError('Please select a vehicle and fuel type');
      return;
    }

    if (!formData.driverName || !formData.driverName.trim()) {
      showError('Driver name is required');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        vehicleId: parseInt(formData.vehicleId),
        driverName: formData.driverName.trim(),
        odometerReading: parseFloat(formData.odometerReading),
        fuelType: formData.fuelType,
        volume: parseFloat(formData.volume),
        fuelDate: formData.fuelDate,
        notes: formData.notes.trim(),
        pricePerLiter: priceInfo?.pricePerLiter || null,
        cost: priceInfo?.pricePerLiter ? (priceInfo.pricePerLiter * parseFloat(formData.volume)) : null
      };

      if (editLog) {
        await apiCall(`/gas/logs/${editLog.FuelLogID}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        showSuccess('Fuel log updated successfully');
      } else {
        const result = await apiCall('/gas/logs', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        
        if (result.costCalculated) {
          showSuccess(`Fuel log added successfully. Cost: ${result.calculatedCost.toFixed(2)} USD`);
        } else {
          showSuccess('Fuel log added. Cost will be calculated when price is set for this date.');
        }
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Prevent scroll from changing number values
  const handleNumberWheel = (e) => {
    e.target.blur(); // Remove focus to prevent value change
    e.preventDefault(); // Prevent scroll
  };

  const selectedVehicle = vehicles.find(v => v.VehicleID === parseInt(formData.vehicleId));
  const totalCost = priceInfo?.pricePerLiter && formData.volume 
    ? (priceInfo.pricePerLiter * parseFloat(formData.volume)).toFixed(2) 
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <style>{`
        /* Hide number input spinners completely */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          margin: 0;
          display: none !important;
        }
        
        /* Remove Firefox spinner */
        input[type=number] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
        
        /* Prevent value change on scroll - extra safety */
        input[type=number]:focus {
          -moz-appearance: textfield;
        }
        
        /* Ensure no spinner shows on hover */
        input[type=number]:hover::-webkit-inner-spin-button,
        input[type=number]:hover::-webkit-outer-spin-button {
          display: none !important;
        }
      `}</style>
      
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-xl font-bold text-gray-800">
            {editLog ? 'Edit Fuel Log' : 'Add Fuel Log'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Vehicle Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Vehicle *
            </label>
            <select
              required
              value={formData.vehicleId}
              onChange={(e) => handleVehicleChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Vehicle</option>
              {vehicles.filter(v => v.IsActive).map(vehicle => (
                <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                  {vehicle.VehicleNumber} - {vehicle.VehicleType} ({vehicle.FuelType})
                  {vehicle.PrimaryDriverName && ` - ${vehicle.PrimaryDriverName}`}
                </option>
              ))}
            </select>
            {selectedVehicle && (
              <p className="mt-1 text-xs text-blue-600">
                ⛽ This vehicle uses: <strong>{selectedVehicle.FuelType}</strong>
                {selectedVehicle.PrimaryDriverName && (
                  <span> | 👨‍✈️ Driver: <strong>{selectedVehicle.PrimaryDriverName}</strong></span>
                )}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Driver Name - Auto-filled but editable */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Driver Name *
              </label>
              <input
                type="text"
                required
                value={formData.driverName}
                onChange={(e) => setFormData({...formData, driverName: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
                placeholder="Auto-filled from vehicle"
                readOnly={!!selectedVehicle?.PrimaryDriverName}
              />
              {selectedVehicle?.PrimaryDriverName && (
                <p className="mt-1 text-xs text-green-600">
                  ✓ Auto-filled from vehicle's primary driver
                </p>
              )}
            </div>

            {/* Fuel Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fuel Date *
              </label>
              <input
                type="date"
                required
                value={formData.fuelDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Odometer Reading - NO SPINNERS, NO SCROLL CHANGE */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Odometer Reading (km) *
              </label>
              <input
                type="number"
                required
                step="0.1"
                value={formData.odometerReading}
                onChange={(e) => setFormData({...formData, odometerReading: e.target.value})}
                onWheel={handleNumberWheel}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 45230.5"
              />
            </div>

            {/* Fuel Type - Auto-filtered */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fuel Type *
              </label>
              <select
                required
                value={formData.fuelType}
                onChange={(e) => handleFuelTypeChange(e.target.value)}
                disabled={!formData.vehicleId || availableFuelTypes.length === 0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Fuel Type</option>
                {availableFuelTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Volume - NO SPINNERS, NO SCROLL CHANGE */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Volume (Liters) *
            </label>
            <input
              type="number"
              required
              step="0.01"
              value={formData.volume}
              onChange={(e) => setFormData({...formData, volume: e.target.value})}
              onWheel={handleNumberWheel}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 50.00"
            />
          </div>

          {/* Price Information - Read-only display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              💰 Pricing Information
              {loadingPrice && (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></span>
              )}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Price per Liter
                </label>
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold">
                  {loadingPrice ? (
                    <span className="text-gray-400">Loading...</span>
                  ) : priceInfo ? (
                    <span className="text-green-700">{priceInfo.pricePerLiter.toFixed(3)} USD</span>
                  ) : (
                    <span className="text-orange-600 italic">Pending</span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Total Cost
                </label>
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold">
                  {totalCost ? (
                    <span className="text-green-700 text-lg">{totalCost} USD</span>
                  ) : (
                    <span className="text-orange-600 italic">Pending Price</span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              ℹ️ Prices are automatically fetched from the fuel pricing database for the selected date
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes <span className="text-gray-500 font-normal">(Optional)</span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Any additional notes..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : (editLog ? 'Update Log' : 'Add Log')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FuelModal;